import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  FormControl,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Paper,
  Stack,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Close as CloseIcon,
  Webhook as WebhookIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Schedule as TimeIcon,
  Refresh as RefreshIcon,
  Analytics as AnalyticsIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  Dns as EndpointIcon,
  TrendingUp as TrendingUpIcon,
  Speed as SpeedIcon,
  Event as EventIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { externalApiService } from '../services/externalApiService';

const WebhookDashboard = ({ open, onClose, webhook }) => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState(30);

  const periodOptions = [
    { value: 7, label: 'Últimos 7 dias' },
    { value: 30, label: 'Últimos 30 dias' },
    { value: 90, label: 'Últimos 90 dias' },
  ];

  const loadStats = async () => {
    if (!webhook?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await externalApiService.getWebhookStats();
      setStats(response);
    } catch (err) {
      console.error('Erro ao carregar estatísticas:', err);
      setError('Erro ao carregar estatísticas. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && webhook?.id) {
      loadStats();
    }
  }, [open, webhook?.id, period]);

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || '0';
  };

  const formatTime = (ms) => {
    if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.round(ms)}ms`;
  };

  // Calcular taxa de sucesso baseada nos dados reais
  const successRate = stats?.total_deliveries_24h > 0 
    ? ((stats.successful_deliveries_24h / stats.total_deliveries_24h) * 100).toFixed(1)
    : 0;

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              <WebhookIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Webhook Analytics
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {webhook?.url || 'Webhook Endpoint'}
                </Typography>
                <Chip 
                  label={webhook?.is_active ? 'Ativo' : 'Inativo'}
                  color={webhook?.is_active ? 'success' : 'error'}
                  size="small"
                />
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                displayEmpty
                sx={{ fontSize: '0.875rem' }}
              >
                {periodOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Atualizar dados">
              <IconButton onClick={loadStats} disabled={loading} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose} sx={{ ml: 1 }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        ) : (
          <Box sx={{ pb: 2 }}>
            {/* Métricas Principais */}
            <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <AssessmentIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Métricas de Entrega
                </Typography>
              </Box>
              
              <Grid container spacing={3}>
                {/* Total de Entregas */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Avatar sx={{ 
                      bgcolor: 'primary.light', 
                      mx: 'auto', 
                      mb: 2,
                      width: 56,
                      height: 56
                    }}>
                      <TrendingUpIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Typography variant="h3" sx={{ 
                      fontWeight: 700, 
                      mb: 1,
                      color: 'primary.main'
                    }}>
                      {formatNumber(stats?.total_deliveries_24h || 0)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Total de Entregas
                    </Typography>
                  </Box>
                </Grid>

                {/* Taxa de Sucesso */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Avatar sx={{ 
                      bgcolor: 'success.light', 
                      mx: 'auto', 
                      mb: 2,
                      width: 56,
                      height: 56
                    }}>
                      <SuccessIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Typography variant="h3" sx={{ 
                      fontWeight: 700, 
                      mb: 1,
                      color: 'success.main'
                    }}>
                      {successRate}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
                      Taxa de Sucesso
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={Number(successRate)}
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        bgcolor: 'success.lighter',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: 'success.main'
                        }
                      }}
                    />
                  </Box>
                </Grid>

                {/* Tempo de Resposta */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Avatar sx={{ 
                      bgcolor: 'info.light', 
                      mx: 'auto', 
                      mb: 2,
                      width: 56,
                      height: 56
                    }}>
                      <SpeedIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Typography variant="h3" sx={{ 
                      fontWeight: 700, 
                      mb: 1,
                      color: 'info.main'
                    }}>
                      {formatTime(stats?.avg_response_time_ms || 0)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Tempo Médio de Resposta
                    </Typography>
                  </Box>
                </Grid>

                {/* Falhas */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Avatar sx={{ 
                      bgcolor: 'error.light', 
                      mx: 'auto', 
                      mb: 2,
                      width: 56,
                      height: 56
                    }}>
                      <ErrorIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Typography variant="h3" sx={{ 
                      fontWeight: 700, 
                      mb: 1,
                      color: 'error.main'
                    }}>
                      {formatNumber(stats?.failed_deliveries_24h || 0)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Entregas Falharam
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Análises Detalhadas */}
            <Grid container spacing={3}>
              {/* Eventos por Tipo */}
              <Grid item xs={12} md={4}>
                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
                  <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EventIcon color="primary" />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Eventos por Tipo
                      </Typography>
                    </Box>
                  </Box>
                  <CardContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {Object.entries(stats?.events_by_type || {}).map(([eventType, count], index) => {
                        const total = Object.values(stats?.events_by_type || {}).reduce((sum, c) => sum + c, 0);
                        const percentage = total > 0 ? (count / total * 100).toFixed(1) : 0;
                        return (
                          <Box key={index}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                                {eventType.replace('.', '.')}
                              </Typography>
                              <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {count}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {percentage}%
                                </Typography>
                              </Box>
                            </Box>
                            <LinearProgress 
                              variant="determinate" 
                              value={Number(percentage)}
                              sx={{ 
                                height: 6, 
                                borderRadius: 3,
                                backgroundColor: 'grey.200',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: 'primary.main'
                                }
                              }}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  </CardContent>
                </Paper>
              </Grid>

              {/* Entregas Recentes */}
              <Grid item xs={12} md={8}>
                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
                  <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TimelineIcon color="primary" />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Histórico de Entregas
                      </Typography>
                    </Box>
                  </Box>
                  <CardContent sx={{ p: 0 }}>
                    <TableContainer sx={{ maxHeight: 300 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Evento</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Tempo Resposta</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Data/Hora</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(stats?.recent_deliveries || []).map((delivery, index) => (
                            <TableRow key={index} hover>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                                  {delivery.event_type}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={delivery.status === 'delivered' ? 'Sucesso' : delivery.status === 'failed' ? 'Falha' : delivery.status}
                                  color={delivery.status === 'delivered' ? 'success' : delivery.status === 'failed' ? 'error' : 'warning'}
                                  size="small"
                                  sx={{ fontSize: '0.7rem', minWidth: 70 }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ 
                                  fontSize: '0.8rem',
                                  color: delivery.response_time_ms > 1000 ? 'warning.main' : 'text.primary'
                                }}>
                                  {formatTime(delivery.response_time_ms)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                  {new Date(delivery.attempted_at).toLocaleString('pt-BR')}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Paper>
              </Grid>

              {/* Informações do Webhook */}
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InfoIcon color="primary" />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Detalhes do Webhook
                      </Typography>
                    </Box>
                  </Box>
                  <CardContent>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            URL do Endpoint:
                          </Typography>
                          <Typography variant="body1" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                            {webhook?.url || 'https://webhook.site/example'}
                          </Typography>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Status:
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Chip 
                              label={webhook?.is_active ? 'Ativo' : 'Inativo'}
                              color={webhook?.is_active ? 'success' : 'error'}
                              size="small"
                            />
                            {webhook?.failure_count > 0 && (
                              <Chip 
                                label={`${webhook.failure_count} falhas`}
                                color="warning"
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Eventos Configurados:
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                            {webhook?.events?.length > 0 ? 
                              webhook.events.map((event, index) => (
                                <Chip key={index} label={event} size="small" variant="outlined" />
                              )) : 
                              <Typography variant="body2" color="text.secondary">
                                Nenhum evento específico configurado
                              </Typography>
                            }
                          </Box>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Descrição:
                          </Typography>
                          <Typography variant="body1">
                            {webhook?.description || 'Sem descrição fornecida'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WebhookDashboard;
